import { oc } from 'ts-optchain';
import _ from 'lodash';

import * as Service from 'types/service';
import {
  AssignmentStatus,
  trackAssignmentCancelled,
  trackAssignmentCompleted,
} from './tableAnalyticEvents';
import { DropTargetConnector, DropTargetMonitor } from 'react-dnd';
import {
  ExperienceToTablesMap,
  RoomToTablesMap,
} from 'components/inventory-schedule/optimization/summary/optimizationSummaryFormAux';
import { Instant, LocalDateTime } from 'js-joda';
import { PartyDragItem } from 'components/service/party/useDraggableParty';
import { SERVER_TABLE_TAG } from 'constants/service/serversConstants';
import { ServicePartyDragMode } from 'state/service/ui';
import { TableIdMap } from 'selectors/routes/calendar/calendarFloorplanSelector';
import { TicketTypeSlimInfo } from 'types/tableBasedTemplates';
import { ZoneId } from '@js-joda/core';
import { dashboard } from 'types';
import { filterNilValues } from '@tock/shared/utils/typeUtils';
import { getExpectedReservationTime } from 'utils/service/walkInUtils';
import { isPartyExpected } from 'utils/partyUtils';
import { sortTableStringsWithinSection } from 'utils/serviceTableUtils';
import PartyStates from 'constants/partyStateConstants';
import TableStates from 'constants/service/tableStateConstants';

export function getOnlySeatedParty(seatedParties: Service.Party[]): Service.Party | undefined {
  return _.size(seatedParties) === 1 ? _.head(seatedParties) : undefined;
}

export function getSoonestPreassignedParty(preassignedParties: Service.Party[]) {
  if (preassignedParties.length < 1) {
    return null;
  }
  return sortPreassignments(preassignedParties)[0];
}

export function sortPreassignments(preassignedParties: Service.Party[]) {
  return _.sortBy(preassignedParties, (party) => {
    return getExpectedReservationTime(party);
  });
}

export function getServerForTable(table: Service.ServiceTable) {
  const serverTag = _.find(table.tag, { key: SERVER_TABLE_TAG });
  return oc(serverTag).val();
}

export function getTableNames(tableIds: number[], tablesById: TableIdMap): string {
  const tableNames = sortTableStringsWithinSection(
    _.filter(
      _.map(tableIds, (tableId) => (tablesById[tableId] ? tablesById[tableId].name : undefined)),
      filterNilValues
    )
  );
  return _.join(tableNames, ', ');
}

export const serviceTableDropTargetFunctions = {
  dropTarget: {
    canDrop(
      props: {
        id: number;
        partySeatedTimeSec?: number | null;
        state: string;
        dragMode: ServicePartyDragMode;
      },
      monitor: DropTargetMonitor<PartyDragItem>
    ) {
      const item = monitor.getItem();
      if (item.mode === 'TIMELINE') {
        return false;
      }

      const { party } = item;
      const { dragMode, partySeatedTimeSec, state: tableState } = props;

      if (dragMode === 'ASSIGN' && party.state === PartyStates.expected) {
        return true;
      }
      if (party.state === PartyStates.expected && _.size(party.tableIds) > 0) {
        return true;
      }
      if (item.dragSource === 'TABLE') {
        return tableState === TableStates.ready;
      }
      return _.isNil(partySeatedTimeSec) && tableState === TableStates.ready;
    },

    drop(
      props: {
        id: number;
        party?: Service.Party;
        partySeatedTimeSec?: number | null;
        onMoveParty(partyId: number, tableId: number, disableAutoSeat: boolean): void;
        onSwapParties(
          droppedParty: Service.Party,
          originalParty: Service.Party,
          fromTableId: number,
          toTableId: number
        ): void;
        sortedPreassignedParties?: Service.Party[];
        timeZone: string;
      },
      monitor: DropTargetMonitor<PartyDragItem>,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      component: any
    ) {
      const item = monitor.getItem();
      if (!item || item.mode !== 'SERVICE') {
        return;
      }
      const { party: dropParty, fromTableId, dragSource } = item;
      if (!dropParty) {
        return;
      }
      if (fromTableId === props.id) {
        trackAssignmentCancelled('service');
        return;
      }

      if (
        fromTableId &&
        dragSource === 'TABLE' &&
        props.party &&
        props.party.state === 'SEATED' &&
        dropParty.state === 'SEATED'
      ) {
        props.onSwapParties(dropParty, props.party, fromTableId, props.id);
      } else {
        props.onMoveParty(dropParty.id, props.id, dragSource === 'TABLE');
      }
      item.onSuccessfulDrop && item.onSuccessfulDrop();

      trackAssignmentCompleted(
        'service',
        dropParty.state,
        getDroppedTableStatus(dropParty, props.sortedPreassignedParties ?? [], props.timeZone)
      );
    },
  },
  collectFunction: (connect: DropTargetConnector, monitor: DropTargetMonitor<PartyDragItem>) => {
    return {
      canDrop: monitor.canDrop(),
      connectDropTarget: connect.dropTarget(),
      isOver: monitor.isOver(),
      dropItem: monitor.getItem(),
    };
  },
};

/**
 * This status value is exclusively used for analytics.
 * It looks to provide insight in to how frequently customers
 * overlap reservations when pre-assigning.
 */
export const getDroppedTableStatus = (
  party: Service.Party,
  preassignedParties: Service.Party[],
  timeZone: string
): AssignmentStatus | undefined => {
  const isExpectedParty = isPartyExpected(party);
  const hasTableAssignments = preassignedParties.length;

  if (!isExpectedParty) {
    return;
  }

  if (!hasTableAssignments) {
    return 'AVAILABLE';
  }

  const allAssignments = preassignedParties.concat(party);
  const sortedAssignments = sortPreassignments(allAssignments);
  const partyIndex = sortedAssignments.findIndex(({ id }) => id === party.id);
  const isOccupied = hasPreassignmentConflict(party, partyIndex, sortedAssignments, timeZone);
  return isOccupied ? 'OCCUPIED' : 'AVAILABLE';
};

/**
 * Preassigned parties are sorted by expected time
 * to detect overlap we see if the previous party will end after the current one starts
 * or if the next party will start before the current one ends
 */
export const hasPreassignmentConflict = (
  party: Service.Party,
  partyIndex: number,
  sortedPreassignedParties: Service.Party[],
  timeZone: string,
  seatedParty?: Service.Party
): boolean => {
  if (seatedParty) {
    sortedPreassignedParties = [seatedParty, ...sortedPreassignedParties];
    partyIndex++;
  }

  if (!sortedPreassignedParties || sortedPreassignedParties.length === 1) {
    return false;
  }

  const previousParty = sortedPreassignedParties[partyIndex - 1];
  const nextParty = sortedPreassignedParties[partyIndex + 1];

  return (
    (previousParty && hasEndTimeConflict(previousParty, party, timeZone)) ||
    (nextParty && hasEndTimeConflict(party, nextParty, timeZone))
  );
};

/**
 * Returns true if the first party ends after the last party starts
 */
export const hasEndTimeConflict = (
  firstParty: Service.Party,
  lastParty: Service.Party,
  timeZone: string
) => {
  const firstPartyExpectedTime = getExpectedReservationLocalDateTime(firstParty, timeZone);
  const lastPartyExpectedTime = getExpectedReservationLocalDateTime(lastParty, timeZone);
  const firstPartyTurnTimeMinutes = getPartyTurnTimeMinutes(firstParty);

  return !!(
    firstPartyTurnTimeMinutes &&
    firstPartyExpectedTime &&
    lastPartyExpectedTime &&
    firstPartyExpectedTime.plusMinutes(firstPartyTurnTimeMinutes).isAfter(lastPartyExpectedTime)
  );
};

/**
 * Returns LocalDateTime containing expected or actual seated time for walk in, or booked time for reservations
 * If there is no arrival time or booked time we return undefined.
 * Returning undefined will render "Walkin" on the table
 */
export const getExpectedReservationLocalDateTime = (party: Service.Party, timeZone: string) => {
  const { expectedReservationTime, quotedTimeMinutes, arrivalTime, seatedTime } = party;
  const quotedMinutes = _.toNumber(quotedTimeMinutes);
  const zoneId = ZoneId.of(timeZone);

  return seatedTime
    ? LocalDateTime.ofInstant(Instant.ofEpochSecond(seatedTime, 0), zoneId)
    : quotedMinutes > 0 && arrivalTime
      ? LocalDateTime.ofInstant(Instant.ofEpochSecond(arrivalTime, 0), zoneId).plusMinutes(
          quotedMinutes
        )
      : expectedReservationTime
        ? LocalDateTime.parse(expectedReservationTime)
        : undefined;
};

/**
 * Returns turn time minutes for a party.  If an override exists we use that first.
 */
export const getPartyTurnTimeMinutes = (party: Service.Party) =>
  party.visit && party.visit.turnTimeSecOverride
    ? party.visit.turnTimeSecOverride / 60
    : party.expectedTurnTimeMinutes;

/**
 * Returns whether or not the table(s) are ready unavailable for seating
 */
export function hasUnavailableTables(tables: Service.ServiceTable[]): boolean {
  return (
    _.isEmpty(tables) ||
    _.some(tables, (table) => table.state === 'Blocked' || table.state === 'Dirty')
  );
}

/**
 * Returns the name to use for displaying a table.
 */
// The logic for this used to be more complicated, and vary depending on whether
// the user was in a retired seating-areas-related FF, but it is still widely-enough
// attested to maintain as a utility function.
export function getTableName(table: { name: string | null } | undefined) {
  return table?.name ?? '';
}

export function createMapOfRoomToTables(tables: dashboard.ITable[]): RoomToTablesMap {
  const roomToTablesMap: RoomToTablesMap = new Map();
  tables.forEach((table) => {
    const { room } = table;
    const effectiveRoom = room ?? 'unnamed room';

    roomToTablesMap.set(effectiveRoom, [...(roomToTablesMap.get(effectiveRoom) ?? []), table]);
  });

  return roomToTablesMap;
}

export function createMapOfExperienceToTables(
  activeTicketTypeSlimInfo: TicketTypeSlimInfo[],
  seatingTemplateTables: dashboard.SeatingTemplateTable[],
  tables: dashboard.ITable[]
): ExperienceToTablesMap {
  const experienceToTablesMap: ExperienceToTablesMap = new Map();
  const ticketTypeIdList: number[] = [];

  const tableIdToTableMap: Map<number, dashboard.ITable> = new Map();

  tables.forEach((table) => tableIdToTableMap.set(table.id as number, table));

  activeTicketTypeSlimInfo.forEach((tt) => {
    tt.ticketTypePriceConfig.forEach((config) => {
      if (config.ticketTypeId) {
        ticketTypeIdList.push(config.ticketTypeId);
      }
    });
  });

  ticketTypeIdList.forEach((ticketTypeId) => {
    const tables: dashboard.ITable[] = [];
    seatingTemplateTables.forEach((stt) => {
      if (stt.ticketTypeId.includes(ticketTypeId)) {
        tables.push(tableIdToTableMap.get(stt.tableId) as dashboard.ITable);
      }
    });
    experienceToTablesMap.set(ticketTypeId, tables);
  });

  return experienceToTablesMap;
}

export function getInactiveTables(
  tables: dashboard.ITable[],
  seatingTemplateTables: dashboard.SeatingTemplateTable[]
): number[] {
  const inactiveTableIds: number[] = [];

  tables.forEach((table) => {
    if (!seatingTemplateTables.find((templateTable) => templateTable.tableId === table.id)) {
      inactiveTableIds.push(table.id as number);
    }
  });

  return inactiveTableIds;
}

export function createMapOfTableIdToTableName(
  tables: dashboard.ITable[],
  seatingTemplateTables: dashboard.SeatingTemplateTable[]
): Map<number, string> {
  const tableIdToTableNameMap: Map<number, string> = new Map();

  tables.forEach((table) => {
    if (seatingTemplateTables.find((templateTable) => templateTable.tableId === table.id)) {
      tableIdToTableNameMap.set(table.id ?? 0, table.name ?? 'unspecified');
    }
  });

  return tableIdToTableNameMap;
}
